import { Modal } from '@/components/modal';
import { modalText } from '@/config/modal';
import { useState } from 'react';
import { useObjectFormDelete } from '@/hooks/use-object-form-delete';
import { useVaultItemDelete } from '@/hooks/use-vault-item-delete';

export function DeleteObject({
  uuid,
  container,
  type,
  onSuccess,
}: {
  uuid: string;
  container: string;
  type?: string;
  onSuccess?: () => void;
}) {
  const isVault = type === 'vault';
  const objectDeleteMutation = useObjectFormDelete();
  const vaultItemDeleteMutation = useVaultItemDelete();
  const isLoading = isVault
    ? vaultItemDeleteMutation.isLoading
    : objectDeleteMutation.isLoading;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function handleDeleteClick() {
    setError(null);
    setIsModalOpen(true);
  }

  function handleCancel() {
    setIsModalOpen(false);
    setError(null);
  }

  function handleObjectDelete() {
    setError(null);

    if (isVault) {
      vaultItemDeleteMutation.mutate(
        { key: uuid },
        {
          onSuccess: () => {
            setIsModalOpen(false);
            onSuccess?.();
          },
          onError: (deleteError: unknown) => {
            setError(
              deleteError instanceof Error
                ? deleteError.message
                : 'Failed to delete vault item'
            );
          },
        }
      );
      return;
    }

    objectDeleteMutation.mutate(
      { uuid, container },
      {
        onSuccess: () => {
          setIsModalOpen(false);
          onSuccess?.();
        },
      }
    );
  }

  return (
    <>
      <button
        onClick={handleDeleteClick}
        type="button"
        className="inline-flex justify-center rounded-md border border-transparent bg-red-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 ml-auto"
      >
        {isLoading ? (
          <>
            <svg
              className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            Processing...
          </>
        ) : (
          <>Delete</>
        )}
      </button>
      {isModalOpen && (
        <Modal
          onCancel={handleCancel}
          onSubmit={handleObjectDelete}
          loading={isLoading}
          title={isVault ? modalText.deleteVaultItemTitle : modalText.deleteNodeTitle}
          message={isVault ? modalText.deleteVaultItemMessage : modalText.deleteNodeMessage}
          actionName={isVault ? modalText.deleteVaultItemActionName : modalText.deleteNodeActionName}
          offsetSidebar={true}
          error={error ?? undefined}
        />
      )}
    </>
  );
}
